import React, { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import useOpenSignedPdfUrl from "src/hooks/useOpenSignedPdfUrl";
import usePdfSignedUrl from "src/hooks/usePdfSignedUrl";

const LONG_PRESS_DELAY_MS = 650;

export default function PdfLinkActions({
  resourceType,
  resourceId,
  label = "PDF Link",
  regenerateLabel = "Force Regenerate PDF",
  size = "small",
  variant = "outlined",
  color,
  fullWidth = false,
  startIcon,
  extraQueryParams = {},
  sx = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const longPressTimerRef = useRef(null);
  const suppressClickRef = useRef(false);
  const { pdfUrl } = usePdfSignedUrl(resourceType, resourceId, {
    enabled: !!resourceType && !!resourceId,
    extraQueryParams,
  });
  const openRegeneratedPdf = useOpenSignedPdfUrl({
    resourceType,
    resourceId,
    extraQueryParams: { ...extraQueryParams, force_pdf_regeneration: true },
  });

  const disabled = !pdfUrl;
  const open = Boolean(anchorEl);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startLongPress = (event) => {
    if (disabled) return;

    const target = event.currentTarget;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      suppressClickRef.current = true;
      setAnchorEl(target);
    }, LONG_PRESS_DELAY_MS);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePrimaryClick = (event) => {
    if (!suppressClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  };

  const handleRegenerate = (event) => {
    handleMenuClose();
    openRegeneratedPdf(event);
  };

  return (
    <>
      <Button
        variant={variant}
        color={color}
        size={size}
        fullWidth={fullWidth}
        startIcon={startIcon}
        disabled={disabled}
        href={pdfUrl || undefined}
        target="_blank"
        onClick={handlePrimaryClick}
        onMouseDown={startLongPress}
        onMouseUp={clearLongPressTimer}
        onMouseLeave={clearLongPressTimer}
        onTouchStart={startLongPress}
        onTouchEnd={clearLongPressTimer}
        sx={sx}
      >
        {label}
      </Button>
      <Menu
        id="pdf-link-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRegenerate}>{regenerateLabel}</MenuItem>
      </Menu>
    </>
  );
}
